<script setup lang="ts">
import { computed, ref, watch, type ComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import UiGroupExpandIcon from "@shared/components/UiGroupExpandIcon/index.vue";
import { useAppStore } from "@shared/store/modules/app";
import { prefetchRouteComponent } from "@shared/router/prefetchRouteComponent";
import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import { collectVisiblePages } from "@shared/utils/sidebarMenu";
import { t } from "@shared/utils/i18n";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

interface SubItem {
    path: string;
    fullPath: string;
    title: string;
}

interface MenuGroupView {
    key: string;
    title: string;
    items: SubItem[];
}

const panelTitle = computed(() => appStore.hoverMenu?.title || "");

function toSubItem(node: SidebarMenuItem): SubItem {
    const path = node.path || "";
    return { path, fullPath: path, title: node.title || path };
}

const menuGroups: ComputedRef<MenuGroupView[]> = computed(() => {
    const menu = appStore.hoverMenu;
    if (!menu) return [];
    return buildTreeGroups(menu);
});

function buildTreeGroups(menu: SidebarMenuItem): MenuGroupView[] {
    const groups: MenuGroupView[] = [];
    for (const child of menu.children ?? []) {
        if (child.type === "group") {
            const items = collectVisiblePages(child).map(toSubItem);
            if (!items.length) continue;
            groups.push({ key: child.id, title: child.title || "", items });
            continue;
        }
        if (child.path) {
            groups.push({ key: child.id, title: "", items: [toSubItem(child)] });
        }
    }
    if (groups.length === 0 && menu.type === "page" && menu.path) {
        groups.push({ key: menu.id, title: "", items: [toSubItem(menu)] });
    }
    return groups;
}

const collapsedGroups = ref<Set<string>>(new Set());

watch(
    () => appStore.hoverMenu?.id,
    () => {
        collapsedGroups.value = new Set();
    },
);

function isGroupExpanded(key: string) {
    return !key || !collapsedGroups.value.has(key);
}

function toggleGroup(key: string, title: string) {
    if (!title) return;
    const next = new Set(collapsedGroups.value);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    collapsedGroups.value = next;
}

function menuPathMatches(currentPath: string, menuPath: string): boolean {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`);
}

/** 当前路径命中的最长菜单 path（避免 /system/announcement 与 /system/announcement/edit 同时高亮） */
function longestMatchingMenuPath(currentPath: string, items: SubItem[]): string {
    let best = "";
    for (const candidate of items) {
        if (!menuPathMatches(currentPath, candidate.fullPath)) continue;
        if (candidate.fullPath.length > best.length) {
            best = candidate.fullPath;
        }
    }
    return best;
}

const isActive = (item: SubItem) => {
    const items = menuGroups.value.flatMap(group => group.items);
    const rawBest = longestMatchingMenuPath(route.path, items);
    if (rawBest) return item.fullPath === rawBest;

    const fallbackPath = (route.meta?.activeMenu as string | undefined) || route.path;
    const fallbackBest = longestMatchingMenuPath(fallbackPath, items);
    return fallbackBest !== "" && item.fullPath === fallbackBest;
};

function handleSubItemHover(item: SubItem) {
    prefetchRouteComponent(router, item.fullPath);
}

function handleClick(item: SubItem) {
    router.push(item.fullPath);
    appStore.closeSubMenu();
}

function handleClose() {
    appStore.closeSubMenu();
}
</script>

<template>
    <transition name="submenu-slide">
        <div v-show="appStore.submenuVisible" class="submenu-panel" @mouseleave="handleClose">
            <div class="panel-title">{{ panelTitle }}</div>
            <div class="panel-body">
                <div v-for="group in menuGroups" :key="group.key" class="menu-group">
                    <!-- 分组：不可跳转；仅三角展开/收起以查看下级菜单 -->
                    <div v-if="group.title" class="group-title">
                        <button
                            type="button"
                            class="group-title__expand"
                            :title="isGroupExpanded(group.key) ? t('收起') : t('展开')"
                            :aria-label="isGroupExpanded(group.key) ? t('收起') : t('展开')"
                            :aria-expanded="isGroupExpanded(group.key)"
                            @click.stop="toggleGroup(group.key, group.title)"
                        >
                            <UiGroupExpandIcon :expanded="isGroupExpanded(group.key)" />
                        </button>
                        <span class="group-title__label">{{ group.title }}</span>
                    </div>
                    <template v-if="isGroupExpanded(group.key)">
                        <div
                            v-for="item in group.items"
                            :key="item.fullPath"
                            class="sub-item"
                            :class="{ 'is-active': isActive(item) }"
                            @mouseenter="handleSubItemHover(item)"
                            @click="handleClick(item)"
                        >
                            {{ item.title }}
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </transition>

    <div v-if="appStore.submenuVisible" class="submenu-mask" @mouseenter="handleClose" />
</template>

<style lang="scss" scoped>
.submenu-panel {
    position: fixed;
    top: 0;
    left: var(--layout-sidebar-width);
    bottom: 0;
    z-index: 1100;
    width: var(--layout-submenu-width);
    background-color: var(--main-background);
    border-left: 1px solid var(--input-border);
    box-shadow: var(--sidebar-submenu-shadow);
    padding: 16px 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    @include scrollbar(4px);
}

.panel-title {
    padding: 0 16px;
    margin-bottom: 19px;
    font-size: var(--font-size-label);
    line-height: var(--line-height-label);
    font-weight: var(--font-weight-regular);
    color: var(--main-color);
}

.panel-body {
    flex: 1;
    padding: 0 8px;
}

.menu-group + .menu-group {
    margin-top: 0;
}

.group-title {
    display: flex;
    align-items: center;
    padding: 0;
    height: 32px;
    line-height: 32px;
    font-size: var(--font-size-label);
    font-weight: var(--font-weight-semibold);
    color: var(--main-color-hover);
    cursor: default;
    user-select: none;
}

.group-title__expand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 6px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    line-height: 1;
}

.group-title__label {
    min-width: 0;
    @include ellipsis;
}

.sub-item {
    height: 32px;
    line-height: 32px;
    padding: 0 0 0 22px;
    font-size: var(--font-size-label);
    color: var(--main-color-hover);
    border-radius: var(--button-radius);
    cursor: pointer;
    user-select: none;
    @include ellipsis;
    transition:
        color var(--transition-base),
        background-color var(--transition-base);

    &:hover {
        background-color: var(--sidebar-submenu-item-hover-bg);
        color: var(--main-color-hover);
    }

    &.is-active {
        color: var(--primary);
        background-color: var(--sidebar-submenu-item-hover-bg);
        font-weight: var(--font-weight-semibold);
    }
}

.submenu-mask {
    position: fixed;
    top: 0;
    bottom: 0;
    left: calc(var(--layout-sidebar-width) + var(--layout-submenu-width));
    right: 0;
    z-index: 1099;
    background: transparent;
}
</style>
